'use client'

import { motion } from 'framer-motion'
import Image from 'next/image'

export default function EnergySolutionOverview() {
  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  }

  return (
    <section className="py-24 md:px-0">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-12 text-center"
        >
          <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
            Die <span className="text-[#ff4500]">Volks-Energielösung</span> auf einen Blick
          </h2>
          <p className="mb-4 text-lg text-gray-600">
            Darstellung PV + Isolierung + Infrarot Plattenheizung
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-8 grid grid-cols-1 items-center gap-x-4 gap-y-8 lg:grid-cols-[1fr_auto_1fr_auto_1fr]"
        >
          {/* Photovoltaik Card */}
          <motion.div
            variants={itemVariants}
            className="relative flex h-full flex-col justify-between overflow-hidden rounded-2xl bg-white p-6 shadow-lg transition-shadow hover:scale-105 hover:shadow-xl hover:transition-transform"
          >
            <div className="absolute -top-4 -right-4 flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-gray-100 shadow-md">
              <div className="relative h-35 w-35">
                <Image
                  src="/images/solar-panel-copy.webp"
                  alt="Photovoltaik"
                  fill
                  className="object-contain"
                  sizes="(max-width: 768px) 60px, 60px"
                />
              </div>
            </div>
            <div>
              <h3 className="mb-4 text-2xl font-bold text-gray-900">Photovoltaik</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>9 kWp</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>20x 450 Wp Glas Glas Bifazial Blackframe Module</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>10.66 kWh Batteriespeicher</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>10.5 kW Wechselrichter</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>E.on Home Box</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>Installation + Inbetriebnahme</span>
                </li>
              </ul>
            </div>
            <div className="mt-6 border-t pt-4">
              <p className="text-xl font-bold text-[#ff4500]">= 13.900 €</p>
            </div>
          </motion.div>

          {/* Plus between Photovoltaik and Isolierung */}
          <div className="justify-center text-center md:flex">
            <span className="text-4xl font-bold text-[#ff4500]">+</span>
          </div>

          {/* Isolierung Card */}
          <motion.div
            variants={itemVariants}
            className="relative flex h-full flex-col justify-between overflow-hidden rounded-2xl bg-white p-6 shadow-lg transition-shadow hover:scale-105 hover:shadow-xl hover:transition-transform"
          >
            <div className="absolute -top-4 -right-4 flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-gray-100 shadow-md">
              <div className="relative h-35 w-35">
                <Image
                  src="/images/final-Schicht.webp"
                  alt="Isolierung"
                  fill
                  className="object-contain"
                  sizes="(max-width: 768px) 60px, 60px"
                />
              </div>
            </div>
            <div>
              <h3 className="mb-4 text-2xl font-bold text-gray-900">Isolierung</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>200 qm² Innenraumfläche</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>Vorbereitungsarbeiten</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>Anwendung durch einen Fachbetrieb</span>
                </li>
              </ul>
            </div>
            <div className="mt-6 border-t pt-4">
              <p className="text-xl font-bold text-[#ff4500]">= 17.000 €</p>
            </div>
          </motion.div>

          {/* Plus between Isolierung and Volksheizung */}
          <div className="justify-center text-center md:flex">
            <span className="text-4xl font-bold text-[#ff4500]">+</span>
          </div>

          {/* Volksheizung Card */}
          <motion.div
            variants={itemVariants}
            className="relative flex h-full flex-col justify-between overflow-hidden rounded-2xl bg-white p-6 shadow-lg transition-shadow hover:scale-105 hover:shadow-xl hover:transition-transform"
          >
            <div className="absolute -top-4 -right-4 flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-gray-100 shadow-md">
              <div className="relative h-35 w-35">
                <Image
                  src="/images/infrared-heater.webp"
                  alt="Volksheizung"
                  fill
                  className="object-contain"
                  sizes="(max-width: 768px) 60px, 60px"
                />
              </div>
            </div>
            <div>
              <h3 className="mb-4 text-2xl font-bold text-gray-900">Volksheizung</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>100 qm² Wohnfläche</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>6 Räume</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>6 Infrarotplatten je 1000 Watt</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>Installation + Versand</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-[#ff4500]">•</span>
                  <span>Smarthome und Regeltechnik</span>
                </li>
              </ul>
            </div>
            <div className="mt-6 border-t pt-4">
              <p className="text-xl font-bold text-[#ff4500]">= 5.838,14 €</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Equals sign before summary */}
        <div className="my-8 flex justify-center">
          <span className="text-5xl font-bold text-[#ff4500]">=</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100 p-8 shadow-lg transition-transform hover:scale-105"
        >
          <h3 className="mb-6 text-center text-2xl font-bold text-gray-900">
            Eine kleine Investition, mit bemerkenswert großer Wirkung!
          </h3>
          <ul className="mx-auto max-w-2xl space-y-3 text-lg">
            <li className="mt-4">
              <div className="items-left flex gap-2">
                <span className="text-lg font-medium text-[#ff4500]">1.</span>
                <span>Stromproduktion mit einem Autarkiegrad von bis zu 85%</span>
              </div>
            </li>
            <li className="mt-4">
              <div className="items-left flex gap-2">
                <span className="text-lg font-medium text-[#ff4500]">2.</span>
                <span>Einen neuen Wandanstrich mit 10 Jahre Garantie</span>
              </div>
            </li>
            <li className="mt-4">
              <div className="items-left flex gap-2">
                <span className="text-lg font-medium text-[#ff4500]">3.</span>
                <span>
                  Eine Isolierung, die bis zu 50 % Energiekosten senkt und die Wände trocken hält
                </span>
              </div>
            </li>
            <li className="mt-4">
              <div className="items-left flex gap-2 md:whitespace-nowrap">
                <span className="text-lg font-medium text-[#ff4500]">4.</span>
                <span>
                  Strom-Wärme-Kopplung: Heizsystem ohne Wartung und mit sehr niedrigem{' '}
                  <br className="md:inline lg:hidden" />
                  Energieverbrauch
                </span>
              </div>
            </li>
          </ul>
        </motion.div>
      </div>
    </section>
  )
}
